import { useEffect, useState, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { graphql, useFragment, useMutation } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import { FileUploadField } from './FileUploadField';
import { ScheduleEditor } from './ScheduleEditor';
import { RssSettingsEditor, DEFAULT_RSS_SETTINGS, type RssSettingsValue } from './RssSettingsEditor';
import { getTopicsConnectionId } from '@/admin/pages/TopicsPage';
import type { TopicForm_topic$key, SourceType, TopicVisibility, TopicType, TtsEngine } from '@/__generated__/TopicForm_topic.graphql';
import type { TopicFormCreateMutation } from '@/__generated__/TopicFormCreateMutation.graphql';
import type { TopicFormUpdateMutation } from '@/__generated__/TopicFormUpdateMutation.graphql';
import type { TopicFormTriggerRssCheckMutation } from '@/__generated__/TopicFormTriggerRssCheckMutation.graphql';
import type { AssetType } from '@/graphql/generated/graphql';

/**
 * Fragment for topic data used in editing
 */
const TopicForm_topic = graphql`
  fragment TopicForm_topic on Topic {
    id
    slug
    name
    sources {
      sourceType
      identifier
      label
      domainFilter
    }
    voiceId
    ttsEngine
    ttsModel
    stylePrompt
    customHostName
    customHostPersonality
    visibility
    topicType
    rssSettings {
      maxItemsPerEpisode
      maxAge
      processingDelay
    }
    cronSchedule
    s3IntroPath
    s3OutroPath
    s3AnchorImagePath
    spotifyUrl
    applePodcastsUrl
  }
`;

/**
 * Mutation to create a new topic
 */
const createTopicMutation = graphql`
  mutation TopicFormCreateMutation($input: CreateTopicInput!) {
    createTopic(input: $input) {
      topic {
        id
        slug
        ...TopicList_topic
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

/**
 * Mutation to update an existing topic
 */
const updateTopicMutation = graphql`
  mutation TopicFormUpdateMutation($slug: String!, $input: UpdateTopicInput!) {
    updateTopic(slug: $slug, input: $input) {
      topic {
        id
        ...TopicForm_topic
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

/**
 * Mutation to trigger RSS check for a topic
 */
const triggerRssCheckMutation = graphql`
  mutation TopicFormTriggerRssCheckMutation($input: TriggerRssCheckInput!) {
    triggerRssCheck(input: $input) {
      success
      feedsChecked
      newContentFound
      ingestionTriggered
      message
      userErrors {
        field
        message
        code
      }
    }
  }
`;

/**
 * Generate a URL-safe slug from a topic name.
 * Example: "MotoGP Daily News" → "motogp-daily-news"
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '')     // Remove invalid characters
    .replace(/-+/g, '-')            // Replace consecutive hyphens with single hyphen
    .replace(/^-|-$/g, '');         // Remove leading/trailing hyphens
}

interface FormData {
  slug: string;
  name: string;
  sources: { sourceType: SourceType; identifier: string; label: string; domainFilter: string }[];
  voiceId: string;
  ttsEngine: TtsEngine;
  ttsModel: string;
  stylePrompt: string;
  customHostName: string;
  customHostPersonality: string;
  visibility: TopicVisibility;
  topicType: TopicType;
  rssSettings?: RssSettingsValue;
  cronSchedule: string;
  s3IntroPath?: string;
  s3OutroPath?: string;
  s3AnchorImagePath?: string;
  spotifyUrl: string;
  applePodcastsUrl: string;
}

interface TopicFormProps {
  topic?: TopicForm_topic$key | null;
  onSuccess: () => void;
  onCancel: () => void;
}

// News source types (excludes RSS - that's for RSS topic type only)
const NEWS_SOURCE_TYPES = [
  { id: 'BING_NEWS' as SourceType, label: 'Bing News', description: 'Bing News RSS feed' },
  { id: 'SERPER_NEWS' as SourceType, label: 'Serper News', description: 'Serper.dev Google News API (requires API key)' },
  // TODO: Re-enable when Google News scraping is fixed
  // { id: 'GOOGLE_NEWS' as SourceType, label: 'Google News', description: 'Google News RSS feed' },
] as const;

// AWS Polly neural voices that support SSML newscaster style for news content
const POLLY_VOICE_OPTIONS = [
  { id: 'Matthew', label: 'Matthew (US Male)' },
  { id: 'Joanna', label: 'Joanna (US Female)' },
  { id: 'Amy', label: 'Amy (UK Female)' },
];

// ElevenLabs default library voice IDs. See https://elevenlabs.io/app/voice-library
const ELEVEN_LABS_VOICE_OPTIONS = [
  { id: '21m00Tcm4TlvDq8ikWAM', label: 'Rachel (US Female, narration)' },
  { id: 'AZnzlk1XvdvUeBnXmlld', label: 'Domi (US Female, strong)' },
  { id: 'ErXwobaYiN019PkySvjV', label: 'Antoni (US Male, soft)' },
  { id: 'VR6AewLTigWG4xSOukaG', label: 'Arnold (US Male, deep)' },
  { id: '29vD33N1CtxCmqQRPOHJ', label: 'Drew (US Male, casual)' },
  { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam (US Male, warm)' },
];

const TTS_ENGINE_OPTIONS = [
  {
    id: 'POLLY' as TtsEngine,
    label: 'Amazon Polly',
    description: 'Async synthesis, SSML marks. Cheapest option.',
  },
  {
    id: 'ELEVEN_LABS' as TtsEngine,
    label: 'ElevenLabs',
    description: 'Higher-quality synthesis, char-level alignment. Falls back to Polly on error.',
  },
] as const;

// ElevenLabs model choices. flash is cheapest + lowest latency; v2 is highest quality.
const ELEVEN_LABS_MODELS = [
  { id: 'eleven_flash_v2_5', label: 'Flash v2.5 (0.5 credit/char, low latency)' },
  { id: 'eleven_turbo_v2_5', label: 'Turbo v2.5 (0.5 credit/char, balanced)' },
  { id: 'eleven_multilingual_v2', label: 'Multilingual v2 (1 credit/char, highest quality)' },
];

const DEFAULT_POLLY_VOICE = 'Matthew';
const DEFAULT_ELEVEN_LABS_VOICE = ELEVEN_LABS_VOICE_OPTIONS[0].id;
const DEFAULT_ELEVEN_LABS_MODEL = 'eleven_flash_v2_5';

const VISIBILITY_OPTIONS = [
  { id: 'PUBLIC' as TopicVisibility, label: 'Public', description: 'Shown on discovery page, accessible by anyone' },
  { id: 'UNLISTED' as TopicVisibility, label: 'Unlisted', description: 'Not shown publicly, but accessible via direct link' },
  { id: 'PRIVATE' as TopicVisibility, label: 'Private', description: 'Only visible to you' },
] as const;

const TOPIC_TYPE_OPTIONS = [
  { id: 'NEWS' as TopicType, label: 'News', description: 'Scheduled cron-based ingestion from news sources' },
  { id: 'RSS' as TopicType, label: 'RSS', description: 'Automated 5-minute polling, generates episodes when new content detected' },
] as const;

export function TopicForm({ topic: topicRef, onSuccess, onCancel }: TopicFormProps) {
  const topic = useFragment(TopicForm_topic, topicRef ?? null);
  const isEditing = !!topic;
  const slug = topic?.slug;

  const [commitCreate, isCreating] = useMutation<TopicFormCreateMutation>(createTopicMutation);
  const [commitUpdate, isUpdating] = useMutation<TopicFormUpdateMutation>(updateTopicMutation);
  const [commitTriggerRssCheck, isCheckingRss] = useMutation<TopicFormTriggerRssCheckMutation>(triggerRssCheckMutation);

  // State for RSS check result message
  const [rssCheckMessage, setRssCheckMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Track whether user has manually edited the slug field
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      slug: '',
      name: '',
      sources: [{ sourceType: 'BING_NEWS', identifier: '', label: '', domainFilter: '' }],
      voiceId: DEFAULT_POLLY_VOICE,
      ttsEngine: 'POLLY',
      ttsModel: '',
      stylePrompt: '',
      customHostName: '',
      customHostPersonality: '',
      visibility: 'PUBLIC',
      topicType: 'NEWS',
      rssSettings: undefined,
      cronSchedule: '',
      spotifyUrl: '',
      applePodcastsUrl: '',
    },
  });

  // Watch the name field for auto-generating slug
  const nameValue = watch('name');

  // Auto-generate slug from name when creating a new topic
  // Only update if the user hasn't manually edited the slug
  useEffect(() => {
    if (!isEditing && !slugManuallyEdited && nameValue) {
      setValue('slug', generateSlug(nameValue));
    }
  }, [nameValue, isEditing, slugManuallyEdited, setValue]);

  // Reset form when topic data loads (for edit mode)
  useEffect(() => {
    if (topic) {
      reset({
        slug: topic.slug,
        name: topic.name,
        sources: topic.sources.map((s) => ({
          sourceType: s.sourceType,
          identifier: s.identifier,
          label: s.label ?? '',
          domainFilter: s.domainFilter ?? '',
        })),
        voiceId: topic.voiceId,
        ttsEngine: topic.ttsEngine,
        ttsModel: topic.ttsModel ?? '',
        stylePrompt: topic.stylePrompt,
        customHostName: topic.customHostName ?? '',
        customHostPersonality: topic.customHostPersonality ?? '',
        visibility: topic.visibility,
        topicType: topic.topicType,
        rssSettings: topic.rssSettings ? {
          maxItemsPerEpisode: topic.rssSettings.maxItemsPerEpisode,
          maxAge: topic.rssSettings.maxAge,
          processingDelay: topic.rssSettings.processingDelay,
        } : undefined,
        cronSchedule: topic.cronSchedule ?? '',
        s3IntroPath: topic.s3IntroPath ?? undefined,
        s3OutroPath: topic.s3OutroPath ?? undefined,
        s3AnchorImagePath: topic.s3AnchorImagePath ?? undefined,
        spotifyUrl: topic.spotifyUrl ?? '',
        applePodcastsUrl: topic.applePodcastsUrl ?? '',
      });
    }
  }, [topic, reset]);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'sources',
  });

  // Watch topic type to reset sources when it changes
  const topicTypeValue = watch('topicType');

  // Watch TTS engine to swap voice list + reset defaults when user changes engine
  const ttsEngineValue = watch('ttsEngine');
  const voiceOptions = ttsEngineValue === 'ELEVEN_LABS' ? ELEVEN_LABS_VOICE_OPTIONS : POLLY_VOICE_OPTIONS;

  // When engine toggles, replace voice + model with engine-appropriate defaults
  // (unless the user is editing an existing topic whose values are already valid
  // for its engine).
  useEffect(() => {
    if (!isEditing) {
      if (ttsEngineValue === 'ELEVEN_LABS') {
        setValue('voiceId', DEFAULT_ELEVEN_LABS_VOICE);
        setValue('ttsModel', DEFAULT_ELEVEN_LABS_MODEL);
      } else {
        setValue('voiceId', DEFAULT_POLLY_VOICE);
        setValue('ttsModel', '');
      }
    }
  }, [ttsEngineValue, isEditing, setValue]);

  // Reset sources when topic type changes (only for new topics)
  useEffect(() => {
    if (!isEditing) {
      if (topicTypeValue === 'RSS') {
        // For RSS topics, reset to a single RSS source
        replace([{ sourceType: 'RSS', identifier: '', label: '', domainFilter: '' }]);
      } else {
        // For NEWS topics, reset to a single Bing News source
        replace([{ sourceType: 'BING_NEWS', identifier: '', label: '', domainFilter: '' }]);
      }
    }
  }, [topicTypeValue, isEditing, replace]);

  // Handle schedule changes from ScheduleEditor
  const handleScheduleChange = useCallback((cronExpression: string) => {
    setValue('cronSchedule', cronExpression);
  }, [setValue]);

  // Handle RSS settings changes
  const handleRssSettingsChange = useCallback((settings: RssSettingsValue) => {
    setValue('rssSettings', settings);
  }, [setValue]);

  // Handle inline updates for file uploads
  const handleInlineUpdate = useCallback((input: Record<string, unknown>) => {
    if (!slug) return;
    commitUpdate({
      variables: {
        slug,
        input,
      },
    });
  }, [slug, commitUpdate]);

  // Handle RSS check button
  const handleCheckRss = useCallback(() => {
    if (!slug) return;
    setRssCheckMessage(null);
    commitTriggerRssCheck({
      variables: {
        input: { topicSlug: slug },
      },
      onCompleted: (response) => {
        const { success, message } = response.triggerRssCheck;
        setRssCheckMessage({
          type: success ? 'success' : 'error',
          text: message ?? (success ? 'RSS check completed' : 'RSS check failed'),
        });
        // Clear message after 10 seconds
        setTimeout(() => setRssCheckMessage(null), 10000);
      },
      onError: (error) => {
        setRssCheckMessage({
          type: 'error',
          text: `Failed to check RSS: ${error.message}`,
        });
      },
    });
  }, [slug, commitTriggerRssCheck]);

  const onSubmit = (data: FormData) => {
    if (isEditing && slug) {
      // Build update input (exclude slug since it can't be changed)
      const updateInput = {
        name: data.name,
        sources: data.sources,
        voiceId: data.voiceId,
        ttsEngine: data.ttsEngine,
        ttsModel: data.ttsEngine === 'ELEVEN_LABS' ? (data.ttsModel || null) : null,
        stylePrompt: data.stylePrompt,
        customHostName: data.customHostName || undefined,
        customHostPersonality: data.customHostPersonality || undefined,
        visibility: data.visibility,
        topicType: data.topicType,
        rssSettings: data.topicType === 'RSS' ? (data.rssSettings ?? DEFAULT_RSS_SETTINGS) : undefined,
        cronSchedule: data.cronSchedule || undefined,
        s3IntroPath: data.s3IntroPath,
        s3OutroPath: data.s3OutroPath,
        s3AnchorImagePath: data.s3AnchorImagePath,
        spotifyUrl: data.spotifyUrl || undefined,
        applePodcastsUrl: data.applePodcastsUrl || undefined,
      };
      commitUpdate({
        variables: { slug, input: updateInput },
        onCompleted: (response) => {
          if (response.updateTopic.userErrors.length === 0) {
            onSuccess();
          } else {
            console.error('Update failed:', response.updateTopic.userErrors);
          }
        },
        onError: (error) => {
          console.error('Failed to update topic:', error);
        },
      });
    } else {
      commitCreate({
        variables: {
          input: {
            slug: data.slug,
            name: data.name,
            sources: data.sources,
            voiceId: data.voiceId,
            ttsEngine: data.ttsEngine,
            ttsModel: data.ttsEngine === 'ELEVEN_LABS' ? (data.ttsModel || undefined) : undefined,
            stylePrompt: data.stylePrompt,
            customHostName: data.customHostName || undefined,
            customHostPersonality: data.customHostPersonality || undefined,
            visibility: data.visibility,
            topicType: data.topicType,
            rssSettings: data.topicType === 'RSS' ? (data.rssSettings ?? DEFAULT_RSS_SETTINGS) : undefined,
            cronSchedule: data.cronSchedule || undefined,
            spotifyUrl: data.spotifyUrl || undefined,
            applePodcastsUrl: data.applePodcastsUrl || undefined,
          },
        },
        updater: (store) => {
          // Get the newly created topic from the mutation response
          const payload = store.getRootField('createTopic');
          if (!payload) return;

          const newTopic = payload.getLinkedRecord('topic');
          if (!newTopic) return;

          // Get the topics connection
          const connectionId = getTopicsConnectionId();
          const connection = store.get(connectionId);

          if (!connection) {
            // Connection not in store yet, will be fetched on next page load
            return;
          }

          // Create a new edge for the topic and prepend it to the connection
          const newEdge = ConnectionHandler.createEdge(
            store,
            connection,
            newTopic,
            'TopicEdge'
          );
          ConnectionHandler.insertEdgeBefore(connection, newEdge);
        },
        onCompleted: (response) => {
          if (response.createTopic.userErrors.length === 0) {
            onSuccess();
          } else {
            console.error('Create failed:', response.createTopic.userErrors);
          }
        },
        onError: (error) => {
          console.error('Failed to create topic:', error);
        },
      });
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            {isEditing ? 'Edit Topic' : 'Create Topic'}
          </h2>
          <p className="mt-1 text-gray-400">
            {isEditing ? 'Update your podcast topic settings' : 'Set up a new podcast topic'}
          </p>
        </div>
        <button type="button" onClick={onCancel} className="btn-secondary w-full sm:w-auto">
          Cancel
        </button>
      </div>

      <div className="card space-y-4">
        <h3 className="font-semibold text-white">Basic Information</h3>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="name" className="label">Name</label>
            <input
              id="name"
              {...register('name', { required: 'Name is required' })}
              className="input"
              placeholder="Claude Code Daily"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="slug" className="label">Slug</label>
            <div className="flex items-center">
              <span className="rounded-l-xl border border-r-0 border-gray-700 bg-gray-800 px-3 py-2.5 text-gray-400 text-sm whitespace-nowrap">
                {window.location.origin}/p/
              </span>
              <input
                id="slug"
                {...register('slug', {
                  required: 'Slug is required',
                  pattern: {
                    value: /^[a-z0-9-]+$/,
                    message: 'Only lowercase letters, numbers, and hyphens',
                  },
                })}
                className="input rounded-l-none"
                placeholder="claude-code-daily"
                disabled={isEditing}
                onInput={() => {
                  // Mark slug as manually edited when user types in the field
                  if (!isEditing) {
                    setSlugManuallyEdited(true);
                  }
                }}
              />
            </div>
            {errors.slug && (
              <p className="mt-1 text-sm text-red-400">{errors.slug.message}</p>
            )}
            {!isEditing && (
              <p className="mt-1 text-xs text-gray-500">
                Auto-generated from name. Edit to customize.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="ttsEngine" className="label">TTS Engine</label>
            <select id="ttsEngine" {...register('ttsEngine')} className="input">
              {TTS_ENGINE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {TTS_ENGINE_OPTIONS.find((o) => o.id === ttsEngineValue)?.description}
            </p>
          </div>

          <div>
            <label htmlFor="voiceId" className="label">Voice</label>
            <select id="voiceId" {...register('voiceId')} className="input">
              {voiceOptions.map((voice) => (
                <option key={voice.id} value={voice.id}>
                  {voice.label}
                </option>
              ))}
            </select>
          </div>

          {ttsEngineValue === 'ELEVEN_LABS' && (
            <div>
              <label htmlFor="ttsModel" className="label">ElevenLabs Model</label>
              <select id="ttsModel" {...register('ttsModel')} className="input">
                {ELEVEN_LABS_MODELS.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="visibility" className="label">Visibility</label>
            <select id="visibility" {...register('visibility')} className="input">
              {VISIBILITY_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {VISIBILITY_OPTIONS.find(o => o.id === watch('visibility'))?.description}
            </p>
          </div>

          <div>
            <label htmlFor="topicType" className="label">Topic Type</label>
            <select id="topicType" {...register('topicType')} className="input">
              {TOPIC_TYPE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {TOPIC_TYPE_OPTIONS.find(o => o.id === watch('topicType'))?.description}
            </p>
          </div>
        </div>

        {watch('topicType') === 'RSS' && (
          <div className="rounded-xl border border-gray-700 bg-gray-800/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-white">RSS Settings</h4>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCheckRss}
                  disabled={isCheckingRss}
                  className="btn-secondary text-sm"
                >
                  {isCheckingRss ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Checking...
                    </>
                  ) : (
                    'Check RSS Now'
                  )}
                </button>
              )}
            </div>
            {rssCheckMessage && (
              <div className={`mb-3 rounded-lg p-3 text-sm ${
                rssCheckMessage.type === 'success'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }`}>
                {rssCheckMessage.text}
              </div>
            )}
            <RssSettingsEditor
              value={watch('rssSettings')}
              onChange={handleRssSettingsChange}
            />
          </div>
        )}

        <div>
          <label htmlFor="stylePrompt" className="label">Style Prompt (optional)</label>
          <textarea
            id="stylePrompt"
            {...register('stylePrompt')}
            className="input min-h-[100px]"
            placeholder="Professional, authoritative, yet conversational news broadcast tone."
          />
          {errors.stylePrompt && (
            <p className="mt-1 text-sm text-red-400">
              {errors.stylePrompt.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="customHostName" className="label">Custom Host Name (optional)</label>
          <input
            id="customHostName"
            {...register('customHostName')}
            className="input"
            placeholder="e.g., Alex Morgan"
          />
          <p className="mt-1 text-xs text-gray-500">
            Give your podcast host a custom name. If left empty, the AI will use a default.
          </p>
        </div>

        <div>
          <label htmlFor="customHostPersonality" className="label">Host Personality (optional)</label>
          <textarea
            id="customHostPersonality"
            {...register('customHostPersonality')}
            className="input min-h-[100px]"
            placeholder="e.g., Enthusiastic and witty tech journalist who loves making complex topics accessible with humor and real-world examples."
          />
          <p className="mt-1 text-xs text-gray-500">
            Describe the host's personality and style for the AI to emulate during script generation.
          </p>
        </div>
      </div>

      {/* Only show schedule editor for NEWS topics - RSS topics use automatic 5-minute polling */}
      {watch('topicType') !== 'RSS' && (
        <div className="card space-y-4">
          <h3 className="font-semibold text-white">Automatic Ingestion Schedule</h3>
          <div className="text-sm text-gray-400">
            Configure when this topic should automatically fetch new content and generate episodes.
          </div>

          <ScheduleEditor
            value={watch('cronSchedule') ?? ''}
            onChange={handleScheduleChange}
          />

          {/* Hidden cron schedule field - controlled by ScheduleEditor above */}
          <input type="hidden" {...register('cronSchedule')} />
        </div>
      )}

      <div className="card space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-semibold text-white">
              {watch('topicType') === 'RSS' ? 'RSS Feeds' : 'Content Sources'}
            </h3>
            {watch('topicType') === 'RSS' && (
              <p className="mt-1 text-sm text-gray-400">
                Add RSS feed URLs to monitor for new content
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() =>
              append({
                sourceType: watch('topicType') === 'RSS' ? 'RSS' : 'BING_NEWS',
                identifier: '',
                label: '',
                domainFilter: ''
              })
            }
            className="btn-secondary text-sm w-full sm:w-auto"
          >
            {watch('topicType') === 'RSS' ? 'Add Feed' : 'Add Source'}
          </button>
        </div>

        {fields.map((field, index) => {
          const isRssTopic = watch('topicType') === 'RSS';
          const sourceType = watch(`sources.${index}.sourceType`);

          return (
            <div
              key={field.id}
              className="flex items-start gap-3 rounded-xl border border-gray-700 bg-gray-800/30 p-4"
            >
              <div className="flex-1 space-y-3">
                {isRssTopic ? (
                  /* RSS Topic: Simplified URL-only input */
                  <div className="grid gap-3 md:grid-cols-2">
                    {/* Hidden field to ensure sourceType is always RSS */}
                    <input type="hidden" {...register(`sources.${index}.sourceType`)} value="RSS" />
                    <div>
                      <label htmlFor={`sources.${index}.identifier`} className="label">Feed URL</label>
                      <input
                        id={`sources.${index}.identifier`}
                        {...register(`sources.${index}.identifier`, {
                          required: 'URL is required',
                          pattern: {
                            value: /^https?:\/\/.+/,
                            message: 'Must be a valid URL starting with http:// or https://'
                          }
                        })}
                        className="input"
                        placeholder="https://example.com/feed.xml"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Full URL to the RSS or Atom feed
                      </p>
                    </div>
                    <div>
                      <label htmlFor={`sources.${index}.label`} className="label">Label (optional)</label>
                      <input
                        id={`sources.${index}.label`}
                        {...register(`sources.${index}.label`)}
                        className="input"
                        placeholder="e.g., TechCrunch"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Display name for this feed
                      </p>
                    </div>
                  </div>
                ) : (
                  /* NEWS Topic: Source type dropdown with search query */
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <label htmlFor={`sources.${index}.sourceType`} className="label">Type</label>
                      <select
                        id={`sources.${index}.sourceType`}
                        {...register(`sources.${index}.sourceType`)}
                        className="input"
                      >
                        {NEWS_SOURCE_TYPES.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      {NEWS_SOURCE_TYPES.find(t => t.id === sourceType) && (
                        <p className="mt-1 text-xs text-gray-500">
                          {NEWS_SOURCE_TYPES.find(t => t.id === sourceType)?.description}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor={`sources.${index}.identifier`} className="label">Search Query</label>
                      <input
                        id={`sources.${index}.identifier`}
                        {...register(`sources.${index}.identifier`, {
                          required: 'Required',
                        })}
                        className="input"
                        placeholder="e.g., electric vehicles"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {sourceType === 'BING_NEWS' && 'Query for Bing News RSS'}
                        {sourceType === 'SERPER_NEWS' && 'Query for Google News API'}
                      </p>
                    </div>

                    <div>
                      <label htmlFor={`sources.${index}.label`} className="label">Label (optional)</label>
                      <input
                        id={`sources.${index}.label`}
                        {...register(`sources.${index}.label`)}
                        className="input"
                        placeholder="Display name"
                      />
                    </div>
                  </div>
                )}
              </div>

              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="mt-6 rounded-lg p-2 text-gray-400 transition-all hover:bg-red-500/20 hover:text-red-400"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Remove source" role="img">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="card space-y-4">
        <h3 className="font-semibold text-white">Media Assets (Optional)</h3>

        {isEditing && slug ? (
          <div className="grid gap-4 md:grid-cols-3">
            <FileUploadField
              label="Intro Audio"
              topicSlug={slug}
              assetType={'INTRO' as AssetType}
              currentPath={topic?.s3IntroPath}
              onUploadComplete={(s3Key) => {
                handleInlineUpdate({ s3IntroPath: s3Key });
              }}
              onClear={() => {
                handleInlineUpdate({ s3IntroPath: '' });
              }}
              accept="audio/mpeg,audio/mp3,audio/wav"
              helpText="MP3 or WAV, max 50MB"
            />

            <FileUploadField
              label="Outro Audio"
              topicSlug={slug}
              assetType={'OUTRO' as AssetType}
              currentPath={topic?.s3OutroPath}
              onUploadComplete={(s3Key) => {
                handleInlineUpdate({ s3OutroPath: s3Key });
              }}
              onClear={() => {
                handleInlineUpdate({ s3OutroPath: '' });
              }}
              accept="audio/mpeg,audio/mp3,audio/wav"
              helpText="MP3 or WAV, max 50MB"
            />

            <FileUploadField
              label="Cover Image"
              topicSlug={slug}
              assetType={'IMAGE' as AssetType}
              currentPath={topic?.s3AnchorImagePath}
              onUploadComplete={(s3Key) => {
                handleInlineUpdate({ s3AnchorImagePath: s3Key });
              }}
              onClear={() => {
                handleInlineUpdate({ s3AnchorImagePath: '' });
              }}
              accept="image/jpeg,image/png,image/webp"
              helpText="JPEG, PNG or WebP, 1400x1400px, max 10MB"
            />
          </div>
        ) : (
          <div className="rounded-xl border border-gray-700 bg-gray-800/30 p-4 text-center text-gray-400">
            <p className="text-sm">
              Save the topic first to upload media assets.
            </p>
          </div>
        )}
      </div>

      <div className="card space-y-4">
        <h3 className="font-semibold text-white">Podcast Platform Links (Optional)</h3>
        <p className="text-sm text-gray-400">
          Add direct links to your podcast on Spotify and Apple Podcasts. If left empty, the buttons won't appear on the topic page.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="spotifyUrl" className="label">Spotify URL</label>
            <input
              id="spotifyUrl"
              {...register('spotifyUrl')}
              className="input"
              placeholder="https://open.spotify.com/show/..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Direct link to your podcast on Spotify
            </p>
          </div>

          <div>
            <label htmlFor="applePodcastsUrl" className="label">Apple Podcasts URL</label>
            <input
              id="applePodcastsUrl"
              {...register('applePodcastsUrl')}
              className="input"
              placeholder="https://podcasts.apple.com/podcast/..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Direct link to your podcast on Apple Podcasts
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : isEditing ? 'Update Topic' : 'Create Topic'}
        </button>
      </div>
    </form>
  );
}
